import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { ConsoleInput } from '../../styles/common/input.styled';
import { Button } from '../../styles/common/Button.styled';
import Modal from '../common/Modal';
import PageState from '../common/PageState';
import { useApi } from '../../hooks/useApi';
import { toast } from 'react-hot-toast';

interface QueueMemberEditModalProps {
  queue: {
    id: number;
    displayName?: string;
    strategy: string;
  };
  onClose: () => void;
  onSuccess: () => void;
}

interface User {
  userId: string;
  displayName: string;
  phone?: string;
}

interface SelectedMember extends User {
  ringOrder: number;
  isEnabled: boolean;
}

interface SettablePhone {
  phone: string;
}

const MainContent = styled.div`
  display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 20px;
  & > [role] { grid-column: 1 / -1; }
  @media (max-width: 640px) { grid-template-columns: minmax(0, 1fr); }
`;
const Section = styled.div`
  min-width: 0; display: flex; flex-direction: column; gap: 12px;
  & + & { border-left: 1px solid ${({ theme }) => theme.color.primary_300}; padding-left: 20px; }
  @media (max-width: 640px) { & + & { border-left: 0; padding-left: 0; border-top: 1px solid ${({ theme }) => theme.color.primary_300}; padding-top: 16px; } }
`;
const SectionTitle = styled.div`
  display: flex; align-items: center; gap: 8px; color: ${({ theme }) => theme.color.text_color_100};
  font-size: 13px; font-weight: 600;
`;
const SearchBox = styled.div`min-width: 0;`;
const ListContainer = styled.div`
  display: flex; flex-direction: column; gap: 4px; min-height: 160px; max-height: 360px; overflow-y: auto;
`;
const MemberItem = styled.div`
  display: flex; justify-content: space-between; align-items: center; gap: 8px;
  min-height: 44px; padding: 5px 8px; border-bottom: 1px solid ${({ theme }) => theme.color.primary_300};
`;
const MemberToggleButton = styled(Button).attrs({ $variant: 'ghost' })<{ $selected?: boolean }>`
  justify-content: space-between; text-align: left; min-height: 44px; height: auto; padding: 5px 8px;
  background: ${({ theme, $selected }) => $selected ? theme.color.primary_50 : 'transparent'};
  border-color: ${({ theme, $selected }) => $selected ? theme.color.primary_400 : 'transparent'};
  white-space: normal;
`;
const MemberInfo = styled.div`display: flex; align-items: center; min-width: 0;`;
const MemberName = styled.span`font-size: 13px; color: ${({ theme }) => theme.color.text_color_100};`;
const MemberExtension = styled.span`font-size: 12px; color: ${({ theme }) => theme.color.text_color_300}; font-variant-numeric: tabular-nums;`;
const MemberStatus = styled.span`margin-left: 6px; font-size: 12px; color: ${({ theme }) => theme.color.text_color_300};`;
const PriorityInput = styled(ConsoleInput)`width: 56px; height: 28px; padding: 0 4px; text-align: center;`;
const TooltipButton = styled(Button).attrs({ $variant: 'ghost', $size: 'sm' })`width: 24px; height: 24px; padding: 0; cursor: help;`;
const TooltipAnchor = styled.span`position: relative; display: inline-flex;`;
const TooltipContent = styled.div`
  position: absolute; top: 28px; right: 0; width: 230px; padding: 10px 12px; z-index: 10;
  background: ${({ theme }) => theme.color.primary_100}; color: ${({ theme }) => theme.color.text_color_100};
  border: 1px solid ${({ theme }) => theme.color.primary_300}; border-radius: 6px;
  box-shadow: 0 4px 16px #00000018; font-size: 12px; line-height: 18px; white-space: normal;
`;
const EmptyState = styled.div`
  padding: 24px 12px; text-align: center; color: ${({ theme }) => theme.color.text_color_300}; font-size: 13px;
`;

const QueueMemberEditModal: React.FC<QueueMemberEditModalProps> = ({ queue, onClose, onSuccess }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<Record<string, SelectedMember>>({});
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [loadedQueueId, setLoadedQueueId] = useState<number | null>(null);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [hasLoadError, setHasLoadError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  const { loading, request } = useApi();

  useEffect(() => {
    let isCurrent = true;

    const fetchData = async () => {
      setLoadedQueueId(null);
      setHasLoadError(false);
      setSelectedMembers({});
      setAllUsers([]);
      try {
        const [detail, settablePhones] = await Promise.all([
          request({ url: `/ringing-group/${queue.id}` }),
          request({ url: '/user/getPhones', params: { isSettable: true } }) as Promise<SettablePhone[]>,
        ]);
        if (!isCurrent) return;

        const settablePhoneNumbers = new Set(
          (Array.isArray(settablePhones) ? settablePhones : []).map(phone => phone.phone)
        );

        const members = (detail?.members || []).reduce((members: Record<string, SelectedMember>, member: any) => {
          members[member.userId] = {
            userId: member.userId,
            displayName: member.displayName,
            ringOrder: member.ringOrder,
            isEnabled: member.isEnabled,
          };
          return members;
        }, {});

        const users: User[] = [];
        let pg = 1;
        let totalPages = 1;
        while (pg <= totalPages) {
          const page = await request({ url: '/user', params: { limit: 100, pg } });
          if (!isCurrent) return;
          users.push(...(page?.content || []).map((user: any) => ({
            userId: user.userId,
            displayName: user.username,
            phone: user.phone,
          })));
          totalPages = page?.totalPages || 1;
          pg += 1;
        }
        if (isCurrent) {
          setSelectedMembers(members);
          setAllUsers(users.filter(user => !!user.phone && settablePhoneNumbers.has(user.phone)));
          setLoadedQueueId(queue.id);
        }
      } catch (error) {
        if (!isCurrent) return;
        console.error('Fetch members error:', error);
        toast.error('데이터를 불러오는데 실패했습니다.');
        setHasLoadError(true);
      }
    };

    fetchData();
    return () => {
      isCurrent = false;
    };
  }, [queue.id, request, retryKey]);

  const isInitialLoading = loadedQueueId !== queue.id;

  const filteredUsers = allUsers.filter(user =>
    user.displayName.includes(searchTerm) || user.phone?.includes(searchTerm)
  );

  const getMemberPhone = (member: SelectedMember) => (
    member.phone || allUsers.find(user => user.userId === member.userId)?.phone || '-'
  );

  const getMemberDisplayName = (member: SelectedMember) => (
    allUsers.find(user => user.userId === member.userId)?.displayName || member.displayName
  );

  const toggleMember = (user: User) => {
    if (selectedMembers[user.userId]) {
      setSelectedMembers(({ [user.userId]: _, ...members }) => members);
    } else {
      setSelectedMembers(members => ({
        ...members,
        [user.userId]: { ...user, ringOrder: 0, isEnabled: true },
      }));
    }
  };

  const handleRingOrderChange = (userId: string, ringOrder: string) => {
    if (ringOrder.trim() === '') return;
    const value = Number(ringOrder);
    if (!Number.isInteger(value) || value < 0) return;

    setSelectedMembers(members => ({
      ...members,
      [userId]: { ...members[userId], ringOrder: value },
    }));
  };

  const handleSave = async () => {
    if (isInitialLoading || hasLoadError) return;
    try {
      const members = Object.values(selectedMembers).map(member => ({
        userId: member.userId,
        ringOrder: member.ringOrder,
        isEnabled: member.isEnabled,
      }));

      await request({
        url: `/ringing-group/${queue.id}/members`,
        method: 'PUT',
        data: members
      });

      toast.success('수신그룹 멤버가 저장되었습니다.');
      onSuccess();
    } catch (error) {
      console.error('Save members error:', error);
      toast.error('멤버 저장에 실패했습니다.');
    }
  };

  return (
    <Modal title={`${queue.displayName || `수신그룹 #${queue.id}`} - 멤버 관리`} onClose={onClose} width={900} historyMode="none" footer={<>
      <Button $variant="secondary" onClick={onClose}>취소</Button>
      <Button onClick={handleSave} disabled={loading || isInitialLoading || hasLoadError}>{loading && !isInitialLoading ? '저장 중...' : '저장하기'}</Button>
    </>}>
        <MainContent>
          {hasLoadError ? (
            <PageState status="error" title="멤버 정보를 불러오지 못했습니다." onRetry={() => setRetryKey(key => key + 1)} />
          ) : isInitialLoading ? (
            <PageState status="loading" title="멤버 정보를 불러오는 중입니다." />
          ) : (
            <>
          <Section>
            <SectionTitle>전체 사용자 <MemberExtension>{filteredUsers.length}명</MemberExtension></SectionTitle>
            <SearchBox>
              <ConsoleInput
                aria-label="사용자 검색"
                name="member-search"
                autoComplete="off"
                placeholder="이름 또는 내선번호 검색…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchBox>
            <ListContainer>
              {filteredUsers.length === 0 ? (
                <EmptyState>표시할 사용자가 없습니다.</EmptyState>
              ) : filteredUsers.map(user => (
                <MemberToggleButton
                  key={user.userId}
                  type="button"
                  onClick={() => toggleMember(user)}
                  $selected={!!selectedMembers[user.userId]}
                  aria-pressed={!!selectedMembers[user.userId]}
                  aria-label={`${user.displayName} 멤버 ${selectedMembers[user.userId] ? '해제' : '선택'}`}
                >
                  <MemberInfo>
                    <MemberName>
                      {user.displayName}
                      {' ('}<MemberExtension>{user.phone || '-'}</MemberExtension>{')'}
                    </MemberName>
                  </MemberInfo>
                  {selectedMembers[user.userId] ? (
                    <span aria-hidden="true">✓</span>
                  ) : (
                    <span aria-hidden="true">+</span>
                  )}
                </MemberToggleButton>
              ))}
            </ListContainer>
          </Section>

          <Section>
            <SectionTitle>
              선택된 멤버 <MemberExtension>{Object.keys(selectedMembers).length}명</MemberExtension>
              {queue.strategy === 'SEQUENTIAL' && (
                <TooltipAnchor>
                  <TooltipButton
                    type="button"
                    aria-label="벨 순서 도움말"
                    aria-controls="ring-order-tooltip"
                    aria-describedby={isTooltipOpen ? 'ring-order-tooltip' : undefined}
                    aria-expanded={isTooltipOpen}
                    onClick={() => setIsTooltipOpen(isOpen => !isOpen)}
                  >
                    ?
                  </TooltipButton>
                  {isTooltipOpen && (
                    <TooltipContent id="ring-order-tooltip" role="tooltip">
                    벨 순서는 낮을수록 먼저 통화를 받습니다.
                    </TooltipContent>
                  )}
                </TooltipAnchor>
              )}
            </SectionTitle>
            <ListContainer>
              {Object.values(selectedMembers).length === 0 ? (
                <EmptyState>선택된 멤버가 없습니다.</EmptyState>
              ) : Object.values(selectedMembers).map(member => (
                <MemberItem key={member.userId} style={{ cursor: 'default' }}>
                  <MemberInfo>
                    <MemberName>
                      {getMemberDisplayName(member)}
                      {' ('}<MemberExtension>{getMemberPhone(member)}</MemberExtension>{')'}
                      {!member.isEnabled && <><span aria-hidden="true"> </span><MemberStatus>(비활성)</MemberStatus></>}
                    </MemberName>
                  </MemberInfo>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {queue.strategy === 'SEQUENTIAL' && <>
                      <MemberExtension>벨 순서:</MemberExtension>
                      <PriorityInput
                        type="number"
                        min="0"
                        step="1"
                        aria-label={`${getMemberDisplayName(member)} 벨 순서`}
                        value={member.ringOrder}
                        onChange={(e) => handleRingOrderChange(member.userId, e.target.value)}
                      />
                    </>}
                    <Button $variant="danger" $size="sm"
                      type="button"
                      aria-label={`${getMemberDisplayName(member)} 멤버 제거`}
                      onClick={() => toggleMember(member)}
                    >
                      &times;
                    </Button>
                  </div>
                </MemberItem>
              ))}
            </ListContainer>
          </Section>
            </>
          )}
        </MainContent>

    </Modal>
  );
};

export default QueueMemberEditModal;
